import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Webinar, UserRole } from '@proref/types';
import { useAuth } from '../contexts/auth-context';
import { useWebinarsQuery, useToggleWebinarReservation, useDeleteWebinar } from '../hooks/use-webinars';
import { speakerNames } from '../lib/webinar-platform';
import { placeholderDim } from '../lib/query-ui';
import { notifyError, notifySuccess } from '../lib/toast';
import { PageHeader } from '../components/shared/PageHeader';
import { Skeleton } from '../components/shared/Skeleton';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';
import { Icon } from '../components/shared/Icons';
import { RouteTabs } from '../components/shared/RouteTabs';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { WebinarDetailModal } from '../components/webinars/WebinarDetailModal';
import { WebinarFormDialog } from './admin/WebinarFormDialog';

interface WebinarsPageProps {
  when: 'upcoming' | 'past';
}

// Format a webinar's start as a readable local date + time, with duration when set. The referee's
// device tz is the right lens for "when do I show up".
function formatWhen(w: Webinar): string {
  const when = new Date(w.scheduled_at).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  return w.duration_min ? `${when} · ${w.duration_min} min` : when;
}

// Whether the actor may MANAGE (delete, or read the attendee list of) THIS webinar — mirrors
// `canManage` in `apps/api/src/modules/webinars/webinars.service.ts` exactly; keep the two in sync.
// This is deliberately narrower than "is a host-type role" (`federation_admin`/`super_admin`): a
// GENERAL webinar (federation_id null) is the ProRef team's, so a federation_admin can SEE it (it's
// visible to everyone) but not manage it. Rendering the delete icon or the attendee roster off the
// coarser role check alone showed both to a federation_admin on a general webinar and then failed
// them — a 403 toast on delete, "Couldn't load attendees" in the modal — for a webinar they were
// never going to be allowed to touch.
function canManageWebinar(role: UserRole | null, actorFederationId: string | null, webinar: Webinar): boolean {
  return role === 'super_admin' || (webinar.federation_id !== null && webinar.federation_id === actorFederationId);
}

// The referee's webinars, upcoming and past — their federation's plus platform-wide general ones.
// Reserve/Cancel is an optimistic toggle, offered only on 'upcoming' (a past webinar has nothing
// left to reserve). A federation_admin (own webinars) or super_admin (general webinars) also gets a
// Schedule button, per-webinar delete (behind a confirm), and the attendee roster in the detail
// modal opened by clicking a row.
export function WebinarsPage({ when }: WebinarsPageProps) {
  const { t } = useTranslation();
  const { role, user } = useAuth();
  // Coarse, role-only check — right for "does this actor host webinars at all" (the Schedule button,
  // the empty-state copy). Per-row delete/attendees need the finer canManageWebinar below instead.
  const canHost = role === 'federation_admin' || role === 'super_admin';
  // Reserving only ever makes sense for something still ahead of you.
  const canReserve = (role === 'referee' || role === 'federation_admin') && when === 'upcoming';
  const isPast = when === 'past';
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, refetch, isPlaceholderData } = useWebinarsQuery(when);
  const webinars = data ?? [];
  const isEmpty = !isLoading && !isError && webinars.length === 0;

  return (
    <>
      <PageHeader
        title={t('webinars.pageTitle')}
        sub={t('webinars.pageSubtitle')}
        actions={
          canHost ? (
            <Button variant="accent" size="sm" onClick={() => setCreateOpen(true)}>
              <Icon.plus className="h-3.5 w-3.5" /> {t('webinars.schedule')}
            </Button>
          ) : undefined
        }
      />

      <RouteTabs
        tabs={[
          { to: '/webinars', label: t('webinars.upcoming'), end: true },
          { to: '/webinars/past', label: t('webinars.past') },
        ]}
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : isError && !data ? (
        <div className="rounded border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('webinars.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : isEmpty ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
          <Icon.users className="h-7 w-7 text-muted" />
          <p className="mt-3 text-sm font-medium text-ink">
            {isPast ? t('webinars.noPastTitle') : t('webinars.noUpcomingTitle')}
          </p>
          <p className="mt-1 text-xs text-muted">
            {isPast
              ? t('webinars.pastEmptyBody')
              : canHost
                ? t('webinars.hostEmptyBody')
                : t('webinars.refereeEmptyBody')}
          </p>
        </div>
      ) : (
        // While switching Upcoming ↔ Past this still holds the other tab's rows; dim them so they
        // read as being replaced rather than as this tab's answer.
        <div className={'space-y-3 ' + placeholderDim(isPlaceholderData)} aria-busy={isPlaceholderData || undefined}>
          {webinars.map((w) => (
            <WebinarRow
              key={w.id}
              webinar={w}
              canReserve={canReserve}
              canManage={canManageWebinar(role, user?.federation_id ?? null, w)}
              // A past webinar is a record, not a draft — see the matching guard (409 WEBINAR_PAST)
              // in the API's deleteWebinar. Hiding the icon here is the UX half of that rule; the
              // API is the half that actually holds if this ever gets out of sync.
              canDelete={!isPast}
            />
          ))}
        </div>
      )}

      {canHost && <WebinarFormDialog open={createOpen} onClose={() => setCreateOpen(false)} isGeneral={role === 'super_admin'} />}
    </>
  );
}

function WebinarRow({
  webinar,
  canReserve,
  canManage,
  canDelete,
}: {
  webinar: Webinar;
  canReserve: boolean;
  canManage: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation();
  const toggle = useToggleWebinarReservation();
  const isFull = webinar.seats_left === 0 && !webinar.is_reserved;
  // The detail modal opened by clicking the row — same view as Home's card, with the attendee
  // roster added on when `canManage` (this specific webinar, not just the actor's role).
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Cancelling only — reserving is a deliberate confirm now (see the Reserve button below), so this
  // toggle mutation only ever runs in the `reserve: false` direction. Backing out of a seat you
  // already hold doesn't need a second "are you sure": that's what Cancel already announces.
  async function onCancel() {
    try {
      await toggle.mutateAsync({ id: webinar.id, reserve: false });
    } catch (err) {
      // Optimistic toggle already rolled back in the hook; surface the failure here.
      notifyError(err, t('webinars.cancelReservationErrorToast'));
    }
  }

  // Speakers when the webinar lists any — they're who a referee actually wants to see — otherwise
  // fall back to the hosting admin, keeping the "hosted by" wording that only fits a host.
  const credit = speakerNames(webinar) ?? (webinar.host_name ? t('webinars.hostedByLower', { name: webinar.host_name }) : null);

  // "18 joining" plus "· N seats left" when capped. Shares home.webinars.joining with the Home
  // card's own meta line — same concept, one key.
  const attendance =
    webinar.seats_left === null
      ? t('home.webinars.joining', { count: webinar.reservation_count })
      : `${t('home.webinars.joining', { count: webinar.reservation_count })} · ${t('webinars.seatsLeft', { count: webinar.seats_left })}`;

  return (
    <>
      {/* The whole row opens the detail modal — same pattern as the exam results table. A keyboard
          user gets there too (tabIndex + Enter), since a clickable <div> is invisible to tab nav. */}
      <div
        className="flex cursor-pointer items-center gap-4 rounded border border-line-2 bg-surface p-4 shadow-1 transition-colors hover:border-line-3"
        onClick={() => setDetailOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setDetailOpen(true);
        }}
        role="button"
        tabIndex={0}
        aria-label={t('webinars.viewAria', { title: webinar.title })}
      >
        {/* Type chip — live vs group at a glance. */}
        <span
          className={
            'grid h-11 w-11 flex-none place-items-center rounded-sm text-[10px] font-semibold uppercase ' +
            (webinar.type === 'live' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700')
          }
        >
          {webinar.type}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="line-clamp-1 text-[15px] font-medium text-ink">{webinar.title}</span>
            {webinar.is_general && (
              <span className="flex-none rounded-sm bg-info/10 px-1.5 py-0.5 text-[10px] font-semibold text-info">
                {t('webinars.general')}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted">
            {formatWhen(webinar)}
            {credit ? ` · ${credit}` : ''}
            {` · ${attendance}`}
          </div>
        </div>

        {/* Stop the row's click here: Delete and Cancel are their own instant actions, not another
            way into the detail modal (Reserve deliberately IS one — see below — but a second
            `setDetailOpen(true)` firing from the row underneath it would just be redundant). */}
        <div className="flex flex-none items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {canReserve && (
            webinar.is_reserved ? (
              <Button variant="default" size="sm" onClick={onCancel} disabled={toggle.isPending}>
                {t('webinars.cancel')}
              </Button>
            ) : (
              // Opens the detail modal to confirm — the same "Reserve your seat" step Home uses —
              // rather than reserving on this one click. A seat is a commitment worth a confirm;
              // Cancel above stays instant because backing out of one isn't.
              <Button variant="accent" size="sm" onClick={() => setDetailOpen(true)} disabled={isFull}>
                {isFull ? t('webinars.full') : t('webinars.reserve')}
              </Button>
            )
          )}
          {/* Edit rides the same rules as delete: only a manager, and only while the webinar is
              still ahead of us (the API refuses a past one with 409 WEBINAR_PAST either way). */}
          {canManage && canDelete && (
            <button
              type="button"
              aria-label={t('webinars.editAria', { title: webinar.title })}
              onClick={() => setEditOpen(true)}
              className="grid h-8 w-8 place-items-center rounded-btn text-muted transition-colors hover:text-ink"
            >
              <Icon.pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canManage && canDelete && <DeleteWebinarDialog webinar={webinar} />}
        </div>
      </div>

      <WebinarDetailModal
        webinar={webinar}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        canReserve={canReserve}
        canManage={canManage}
      />

      {canManage && canDelete && (
        <WebinarFormDialog webinar={webinar} open={editOpen} onClose={() => setEditOpen(false)} />
      )}
    </>
  );
}

// Delete-confirm, mirroring the admin exams list's pattern: a destructive action needs an explicit
// "are you sure", not a bare trash icon that fires on the first click.
function DeleteWebinarDialog({ webinar }: { webinar: Webinar }) {
  const { t } = useTranslation();
  const remove = useDeleteWebinar();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await remove.mutateAsync(webinar.id);
      notifySuccess(t('webinars.deletedToast'));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('webinars.deleteErrorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          aria-label={t('webinars.deleteAria', { title: webinar.title })}
          className="grid h-8 w-8 place-items-center rounded-btn text-muted transition-colors hover:text-danger"
        >
          <Icon.trash className="h-3.5 w-3.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('webinars.deleteConfirmTitle', { title: webinar.title })}</AlertDialogTitle>
          <AlertDialogDescription>
            {webinar.reservation_count > 0
              ? t('webinars.deleteConfirmBodyWithReservations', { count: webinar.reservation_count })
              : t('webinars.deleteConfirmBodyNoReservations')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={remove.isPending}>
            {t('webinars.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="h-3.5 w-3.5" /> : t('webinars.deleteWebinarButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
